import { useMemo } from 'react';

import { useSafeIsFocused } from '@sg/garnish';

import { useContentfulContentTypeEntries } from '@order/Contentful';

// ─────────────────────────────────────────────────────────────────────────────

export const useMenuContent = () => {
  const isScreenFocused = useSafeIsFocused();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { data, fetching: isFetchingMenuContent } = useContentfulContentTypeEntries<Content>({
    contentType: CONTENT_TYPE,
    pause: !isScreenFocused,
  });

  const fields = data?.[0]?.fields ?? FALLBACK_CONTENT;

  // ─── Derived Data ────────────────────────────────────────────────────

  const mappedContent = useMemo<MappedContent>(() => {
    const categoriesBadges = fields.categoriesBadges;

    return {
      categoriesBadges: Object.fromEntries(
        categoriesBadges.filter(checkIfValidCategoryBadge).map(parseCategoryBadge),
      ),
    };
  }, [fields]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    mappedContent,
    isFetchingMenuContent,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfValidCategoryBadge(entry: string) {
  return CATEGORY_BADGE_PATTERN.test(entry);
}

function parseCategoryBadge(entry: string): [string, string] {
  const [id, badge] = entry.split('-');

  return [id, badge];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTENT_TYPE = 'orderMenuScreenContent';
const CATEGORY_BADGE_PATTERN = /^\d+-[A-Za-z0-9 ]+$/; // mirrors Contentful rule. just an extra security layer.

const FALLBACK_CONTENT: Content = {
  categoriesBadges: [],
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Content = {
  categoriesBadges: string[];
};

type MappedContent = {
  categoriesBadges: Record<string, string>;
};
